import {DataSection} from '../typedef';

type AlertItem = {
    contentId: string | number,
    alertFrequency: string,
};

/**
 * Generic merger for saved content (searches/lists).
 * Returns DataSection with the same shape expected by the UI.
 */
export const mergeSavedContentData = <TItem, TCount>(
    items: TItem[] = [],
    alerts: AlertItem[] = [],
    countsData: TCount[] = [],
    options: {
        getId: (item: TItem) => string | number,
        getName: (item: TItem) => string,
        getSearchType: (item: TItem) => string,
        getAuditStatistic?: (item: TItem) => {
            createdAt?: string,
            lastRunAt?: string,
            lastSharedAt?: string,
            lastUpdatedAt?: string,
        } | undefined,
        getCountKey: (count: TCount) => string | number,
        getCountValues: (count: TCount) => {alertCount: number, totalCount: number},
    },
): DataSection => {
    const alertsMap: {[key: string]: AlertItem} = {};
    (alerts || []).forEach((alertItem: AlertItem) => {
        alertsMap[String(alertItem.contentId)] = alertItem;
    });

    const countsMap: {[key: string]: {alertCount: number, totalCount: number}} = {};
    const countsArray = Array.isArray(countsData) ? countsData : [];

    countsArray.forEach((countItem: TCount) => {
        const key = String(options.getCountKey(countItem));
        countsMap[key] = options.getCountValues(countItem);
    });

    const merged: DataSection = {};
    (items || []).forEach((item: TItem, idx: number) => {
        const id = options.getId(item);
        const counts = countsMap[String(id)] || {alertCount: 0, totalCount: 0};
        const audit = options.getAuditStatistic?.(item);

        merged[`saved_${idx}`] = {
            savedSearchInfo: {
                id,
                name: options.getName(item),
                searchType: options.getSearchType(item),
                createdAt: audit?.createdAt || '',
                lastRunAt: audit?.lastRunAt || '',
                lastSharedAt: audit?.lastSharedAt || '',
                lastUpdatedAt: audit?.lastUpdatedAt || '',
                alertCount: counts.alertCount,
                searchTotalCount: counts.totalCount,
                alert: alertsMap[String(id)] ? alertsMap[String(id)].alertFrequency : undefined,
            },
        };
    });

    return merged;
};
